import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/revenue/reconciliation`;

// ── Types ─────────────────────────────────────────────────

interface DeferredEntityRow { entity_code: string; committed: number; recognized: number; deferred: number; pct_recognized: number; contract_count: number; }
interface DeferredContractRow { contract_id: number; contract_number: string; customer_name: string; entity_code: string; is_msa: boolean; committed: number; recognized: number; deferred: number; pct_recognized: number; pob_count: number; }
interface DeferredReport {
  total_committed: number; total_recognized: number; total_deferred: number; pct_recognized: number; contract_count: number;
  by_entity: DeferredEntityRow[]; contracts: DeferredContractRow[];
}

interface UnbilledEntityRow { entity_code: string; unbilled: number; pob_count: number; }
interface UnbilledPobRow { obligation_id: number; contract_id: number; contract_number: string; customer_name: string; entity_code: string; recognition_method: string; description: string; allocated: number; recognized: number; invoiced: number; unbilled: number; }
interface UnbilledReport {
  total_unbilled: number; pob_count: number;
  by_entity: UnbilledEntityRow[]; obligations: UnbilledPobRow[];
}

interface VarianceContractRow { contract_id: number; contract_number: string; customer_name: string; entity_code: string; committed: number; invoiced: number; recognized: number; variance: number; variance_type: string; pct_billed: number; pct_recognized: number; }
interface UnlinkedInvoiceRow { invoice_id: number; invoice_number: string; invoice_date: string | null; customer_name: string; entity_code: string; net_amount: number; }
interface VarianceReport {
  total_invoiced: number; total_recognized: number; total_variance: number;
  contracts: VarianceContractRow[];
  unlinked_invoices: { count: number; total_amount: number; invoices: UnlinkedInvoiceRow[]; };
}

// Phase 13b — contract-related assets breakdown
interface RelatedEntityRow { entity_code: string; contract_asset: number; capitalized_acquisition_cost: number; capitalized_fulfillment_cost: number; contract_count: number; }
interface RelatedContractRow { contract_id: number; contract_number: string; customer_name: string; entity_code: string; contract_asset: number; capitalized_acquisition_cost: number; capitalized_fulfillment_cost: number; has_impairment_history: boolean; total: number; }
interface RelatedReport {
  total_contract_asset: number; total_capitalized_acquisition_cost: number; total_capitalized_fulfillment_cost: number; total_all_categories: number; contract_count: number;
  no_netting_notice: string;
  by_entity: RelatedEntityRow[]; contracts: RelatedContractRow[];
}

// ── Columns ───────────────────────────────────────────────

const deferredEntityCols: Column[] = [
  { key: 'entity_code', label: 'Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'contract_count', label: 'Contracts', align: 'right' },
  { key: 'committed', label: 'Committed', align: 'right', render: (v: number) => formatAmount(v) },
  { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
  { key: 'deferred', label: 'Deferred', align: 'right', render: (v: number) => <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{formatAmount(v)}</span> },
  { key: 'pct_recognized', label: '% Rec.', align: 'right', render: (v: number) => <span>{v}%</span> },
];

const unbilledEntityCols: Column[] = [
  { key: 'entity_code', label: 'Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'pob_count', label: 'POBs', align: 'right' },
  { key: 'unbilled', label: 'Unbilled', align: 'right', render: (v: number) => <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{formatAmount(v)}</span> },
];

const unlinkedCols: Column[] = [
  { key: 'invoice_number', label: 'Invoice', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
  { key: 'invoice_date', label: 'Date', render: (v: string) => v || '-' },
  { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
  { key: 'entity_code', label: 'Entity' },
  { key: 'net_amount', label: 'Amount', align: 'right', render: (v: number) => formatAmount(v) },
];

const varianceTypeBadge = (t: string) => {
  const map: Record<string, { bg: string; color: string }> = {
    MATCHED: { bg: 'rgba(34,197,94,0.12)', color: 'var(--positive)' },
    BILLED_AHEAD: { bg: 'rgba(234,179,8,0.15)', color: 'var(--warning)' },
    RECOGNIZED_AHEAD: { bg: 'rgba(59,130,246,0.15)', color: 'var(--accent)' },
  };
  const s = map[t] || { bg: 'var(--bg-hover)', color: 'var(--text-secondary)' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: s.bg, color: s.color, fontSize: 11, fontWeight: 600 }}>
      {t.replace('_', ' ')}
    </span>
  );
};

export default function RevenueReconciliation() {
  const navigate = useNavigate();
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('deferred');
  const [loading, setLoading] = useState(true);
  const [deferred, setDeferred] = useState<DeferredReport | null>(null);
  const [unbilled, setUnbilled] = useState<UnbilledReport | null>(null);
  const [variance, setVariance] = useState<VarianceReport | null>(null);
  const [related, setRelated] = useState<RelatedReport | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defRes, unbRes, varRes, relRes] = await Promise.all([
        authFetch(withEntityParam(`${API}/deferred`, entity)),
        authFetch(withEntityParam(`${API}/unbilled`, entity)),
        authFetch(withEntityParam(`${API}/variance`, entity)),
        authFetch(withEntityParam(`${API}/contract-related-assets`, entity)),
      ]);
      if (defRes.ok) setDeferred(await defRes.json());
      if (unbRes.ok) setUnbilled(await unbRes.json());
      if (varRes.ok) setVariance(await varRes.json());
      if (relRes.ok) setRelated(await relRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'deferred', label: 'Deferred Revenue' },
    { key: 'unbilled', label: 'Unbilled Revenue' },
    { key: 'variance', label: 'Billing vs Recognition' },
    { key: 'related', label: 'Contract-Related Assets' },
  ];

  // Row-level columns with drill-down via navigate(contract_number)
  const contractLink = (v: string, row: any) => (
    <a onClick={(e) => { e.preventDefault(); navigate(`/revenue-contracts?search=${encodeURIComponent(v)}`); }}
       style={{ color: 'var(--accent)', fontWeight: 600, cursor: 'pointer' }}>{v}</a>
  );

  const deferredContractCols: Column[] = [
    { key: 'contract_number', label: 'Contract', render: contractLink },
    { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
    { key: 'entity_code', label: 'Entity' },
    { key: 'is_msa', label: 'Type', render: (v: boolean) => v ? <span style={{ fontSize: 11, color: 'var(--accent)' }}>MSA</span> : <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Direct</span> },
    { key: 'pob_count', label: 'POBs', align: 'right' },
    { key: 'committed', label: 'Committed', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'deferred', label: 'Deferred', align: 'right', render: (v: number) => <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'pct_recognized', label: '% Rec.', align: 'right', render: (v: number) => <span>{v}%</span> },
  ];

  const unbilledPobCols: Column[] = [
    { key: 'contract_number', label: 'Contract', render: contractLink },
    { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
    { key: 'entity_code', label: 'Entity' },
    { key: 'recognition_method', label: 'Method', render: (v: string) => <span style={{ fontSize: 11 }}>{v}</span> },
    { key: 'description', label: 'Obligation', render: (v: string) => v || '-' },
    { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'invoiced', label: 'Invoiced', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'unbilled', label: 'Unbilled', align: 'right', render: (v: number) => <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{formatAmount(v)}</span> },
  ];

  const varianceContractCols: Column[] = [
    { key: 'contract_number', label: 'Contract', render: contractLink },
    { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
    { key: 'entity_code', label: 'Entity' },
    { key: 'invoiced', label: 'Invoiced', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'recognized', label: 'Recognized', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'variance', label: 'Variance', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: v === 0 ? 'var(--text-secondary)' : v > 0 ? 'var(--warning)' : 'var(--accent)' }}>{formatAmount(v)}</span> },
    { key: 'variance_type', label: 'Type', render: (v: string) => varianceTypeBadge(v) },
  ];

  // Phase 13b — contract-related assets columns (ASC 606-10-45-1 + 340-40-45-1)
  const relatedEntityCols: Column[] = [
    { key: 'entity_code', label: 'Entity', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'contract_count', label: 'Contracts', align: 'right' },
    { key: 'contract_asset', label: 'Contract Asset', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'capitalized_acquisition_cost', label: 'Cap. Acquisition', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'capitalized_fulfillment_cost', label: 'Cap. Fulfillment', align: 'right', render: (v: number) => formatAmount(v) },
  ];

  const relatedContractCols: Column[] = [
    { key: 'contract_number', label: 'Contract', render: contractLink },
    { key: 'customer_name', label: 'Customer', render: (v: string) => v || '-' },
    { key: 'entity_code', label: 'Entity' },
    { key: 'contract_asset', label: 'Contract Asset', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--accent)' : 'var(--text-hint)' }}>{formatAmount(v)}</span> },
    { key: 'capitalized_acquisition_cost', label: 'Cap. Acq.', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--positive)' : 'var(--text-hint)' }}>{formatAmount(v)}</span> },
    { key: 'capitalized_fulfillment_cost', label: 'Cap. Ful.', align: 'right', render: (v: number) => <span style={{ color: v > 0 ? 'var(--positive)' : 'var(--text-hint)' }}>{formatAmount(v)}</span> },
    { key: 'has_impairment_history', label: 'Impairment', render: (v: boolean) => v ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: 'rgba(234,179,8,0.15)', color: 'var(--warning)', fontSize: 11, fontWeight: 600 }}>FLAGGED</span> : <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>—</span> },
    { key: 'total', label: 'Total', align: 'right', render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
  ];

  return (
    <PageLayout
      title="Revenue Reconciliation"
      subtitle="Deferred revenue, unbilled revenue, and billing-vs-recognition variance (ASC 606 operational)"
    >
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</div>
      ) : tab === 'deferred' ? (
        <>
          {deferred && (
            <KpiGrid columns={4} items={[
              { label: 'Contracts', value: deferred.contract_count },
              { label: 'Committed', value: formatAmountCompact(deferred.total_committed), color: 'var(--accent)' },
              { label: 'Recognized', value: formatAmountCompact(deferred.total_recognized), color: 'var(--positive)' },
              { label: 'Deferred', value: formatAmountCompact(deferred.total_deferred), color: 'var(--warning)' },
            ]} />
          )}
          {deferred && deferred.by_entity.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Entity</div>
              <DataTable columns={deferredEntityCols} rows={deferred.by_entity} rowKey="entity_code" emptyMessage="No entity data." />
            </div>
          )}
          {deferred && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Contract</div>
              <DataTable columns={deferredContractCols} rows={deferred.contracts} rowKey="contract_id" emptyMessage="No contracts with deferred revenue." />
            </div>
          )}
        </>
      ) : tab === 'unbilled' ? (
        <>
          {unbilled && (
            <KpiGrid columns={2} items={[
              { label: 'Total Unbilled', value: formatAmountCompact(unbilled.total_unbilled), color: 'var(--accent)' },
              { label: 'POBs', value: unbilled.pob_count },
            ]} />
          )}
          {unbilled && unbilled.by_entity.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Entity</div>
              <DataTable columns={unbilledEntityCols} rows={unbilled.by_entity} rowKey="entity_code" emptyMessage="No entity data." />
            </div>
          )}
          {unbilled && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Performance Obligations</div>
              <DataTable columns={unbilledPobCols} rows={unbilled.obligations} rowKey="obligation_id" emptyMessage="All POBs are billed ahead of or matched to recognition." />
            </div>
          )}
        </>
      ) : tab === 'variance' ? (
        <>
          {variance && (
            <KpiGrid columns={4} items={[
              { label: 'Invoiced', value: formatAmountCompact(variance.total_invoiced) },
              { label: 'Recognized', value: formatAmountCompact(variance.total_recognized), color: 'var(--positive)' },
              { label: 'Net Variance', value: formatAmountCompact(variance.total_variance), color: variance.total_variance === 0 ? 'var(--text-secondary)' : variance.total_variance > 0 ? 'var(--warning)' : 'var(--accent)' },
              { label: 'Unlinked Invoices', value: `${variance.unlinked_invoices.count} / ${formatAmountCompact(variance.unlinked_invoices.total_amount)}`, color: variance.unlinked_invoices.count > 0 ? 'var(--warning)' : 'var(--text-secondary)' },
            ]} />
          )}
          {variance && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Contract</div>
              <DataTable columns={varianceContractCols} rows={variance.contracts} rowKey="contract_id" emptyMessage="No contract variance data." />
            </div>
          )}
          {variance && variance.unlinked_invoices.count > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--warning)' }}>
                Unlinked Invoices — need revenue contract linkage for full variance recon
              </div>
              <DataTable columns={unlinkedCols} rows={variance.unlinked_invoices.invoices} rowKey="invoice_id" emptyMessage="None." />
            </div>
          )}
        </>
      ) : (
        <>
          {related && (
            <KpiGrid columns={4} items={[
              { label: 'Contract Asset', value: formatAmountCompact(related.total_contract_asset), color: 'var(--accent)' },
              { label: 'Cap. Acquisition Cost', value: formatAmountCompact(related.total_capitalized_acquisition_cost), color: 'var(--positive)' },
              { label: 'Cap. Fulfillment Cost', value: formatAmountCompact(related.total_capitalized_fulfillment_cost), color: 'var(--positive)' },
              { label: 'Contracts', value: related.contract_count },
            ]} />
          )}
          {related && related.no_netting_notice && (
            <div style={{ padding: '10px 14px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
              <span style={{ fontWeight: 600, color: 'var(--accent)' }}>ASC presentation rule: </span>
              {related.no_netting_notice}
            </div>
          )}
          {related && related.by_entity.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Entity</div>
              <DataTable columns={relatedEntityCols} rows={related.by_entity} rowKey="entity_code" emptyMessage="No entity data." />
            </div>
          )}
          {related && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>By Contract</div>
              <DataTable columns={relatedContractCols} rows={related.contracts} rowKey="contract_id" emptyMessage="No contracts with contract-related asset balances." />
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
}
